/**
 * Which buckets of a week/month chart cover only part of their calendar period.
 *
 * A bucket is incomplete when the requested range cuts it short (the first
 * bucket starts after the period's first day, or the last ends before the
 * period's last day) or when the period has not ended yet (its last day is
 * today or later).
 *
 * Everything is a UTC `YYYY-MM-DD` string — the backend buckets in UTC, and
 * string math keeps the result off the browser's timezone. No imports, so the
 * unit tests can load this file on its own.
 */
export type IncompletePeriodGranularity = 'week' | 'month';

export type CoveredRange = { start: string; end: string };

export type IncompletePeriods = {
  granularity: IncompletePeriodGranularity;
  /** One flag per chart category, in order. */
  flags: boolean[];
  /** The days each flagged category actually covers (null when not flagged). */
  ranges: (CoveredRange | null)[];
};

const DAY_MS = 86_400_000;
const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const toMs = (iso: string) => Date.parse(`${iso}T00:00:00Z`);
const toIso = (ms: number) => new Date(ms).toISOString().slice(0, 10);

export const utcToday = () => new Date().toISOString().slice(0, 10);

/** First and last day of the calendar week (Mon–Sun) or month holding `iso`. */
export const calendarPeriod = (
  iso: string,
  granularity: IncompletePeriodGranularity
) => {
  const ms = toMs(iso);
  if (granularity === 'week') {
    const sinceMonday = (new Date(ms).getUTCDay() + 6) % 7;
    const start = ms - sinceMonday * DAY_MS;
    return { start: toIso(start), end: toIso(start + 6 * DAY_MS) };
  }
  const d = new Date(ms);
  const year = d.getUTCFullYear();
  const month = d.getUTCMonth();
  return {
    start: toIso(Date.UTC(year, month, 1)),
    end: toIso(Date.UTC(year, month + 1, 0)),
  };
};

type RangeParams = {
  /** Requested range. When absent, the first/last bucket edges stand in for it. */
  from?: string;
  to?: string;
  granularity: 'day' | 'week' | 'month';
  bucketCount: number;
  /** Start of the first bucket and end of the last one (any day inside works). */
  firstBucketStart: string;
  lastBucketEnd: string;
  today?: string;
};

/**
 * For charts that know the requested range: only the first and last bucket can
 * be cut short, and only the last can still be in progress. Day charts get none.
 */
export const getIncompletePeriods = ({
  from,
  to,
  granularity,
  bucketCount,
  firstBucketStart,
  lastBucketEnd,
  today = utcToday(),
}: RangeParams): IncompletePeriods | undefined => {
  if (granularity === 'day' || bucketCount === 0) return undefined;

  const rangeFrom = from ?? firstBucketStart;
  const rangeTo = to ?? lastBucketEnd;
  const last = bucketCount - 1;
  const flags = Array.from({ length: bucketCount }, () => false);
  const ranges: (CoveredRange | null)[] = flags.map(() => null);

  if (rangeFrom > calendarPeriod(firstBucketStart, granularity).start) {
    flags[0] = true;
  }
  const lastPeriodEnd = calendarPeriod(lastBucketEnd, granularity).end;
  if (rangeTo < lastPeriodEnd || lastPeriodEnd >= today) {
    flags[last] = true;
  }

  // The days a cut-short bucket really holds: the range edges clip the
  // calendar period, and nothing exists past today.
  const covered = (period: CoveredRange): CoveredRange => ({
    start: period.start > rangeFrom ? period.start : rangeFrom,
    end: [period.end, rangeTo, today].reduce((a, b) => (a < b ? a : b)),
  });
  if (flags[0])
    ranges[0] = covered(calendarPeriod(firstBucketStart, granularity));
  if (flags[last]) {
    ranges[last] = covered(calendarPeriod(lastBucketEnd, granularity));
  }

  return flags.some(Boolean) ? { granularity, flags, ranges } : undefined;
};

/**
 * For the month-over-month chart, which only ships "MMM YYYY" labels. Its
 * window starts on the 1st, so only the current month can be incomplete.
 */
export const getIncompleteMonthLabels = (
  labels: string[],
  today = utcToday()
): IncompletePeriods | undefined => {
  const ranges = labels.map((label): CoveredRange | null => {
    const [name, year] = label.split(' ');
    const month = MONTHS.indexOf(name);
    if (month < 0 || !/^\d{4}$/.test(year)) return null;
    const period = calendarPeriod(toIso(Date.UTC(+year, month, 1)), 'month');
    return period.end < today ? null : { start: period.start, end: today };
  });
  const flags = ranges.map(Boolean);

  return flags.some(Boolean)
    ? { granularity: 'month', flags, ranges }
    : undefined;
};

/**
 * "Oct 05, 2026 — Oct 07, 2026": the same text the weekly charts already use.
 * `padDay` and `dash` let a chart match its own week labels (the Levels of Care
 * ones come from the backend as "Sep 8, 2026 – Sep 14, 2026").
 */
export const formatCoveredRange = (
  { start, end }: CoveredRange,
  { padDay = true, dash = '—' }: { padDay?: boolean; dash?: string } = {}
) => {
  const part = (iso: string) => {
    const [year, month, day] = iso.split('-');
    const dayText = padDay ? day : String(Number(day));
    return `${MONTHS[Number(month) - 1]} ${dayText}, ${year}`;
  };
  return `${part(start)} ${dash} ${part(end)}`;
};

/**
 * Tooltip headers for the incomplete months of a chart: the days the data
 * covers instead of "Oct '25". Everything else is left to the chart's own label.
 */
export const getIncompleteMonthTitles = (
  incompletePeriods: IncompletePeriods | undefined,
  format?: Parameters<typeof formatCoveredRange>[1]
): (string | undefined)[] | undefined => {
  if (incompletePeriods?.granularity !== 'month') return undefined;
  return incompletePeriods.ranges.map((range) =>
    range ? formatCoveredRange(range, format) : undefined
  );
};
